import {merge} from 'rxjs';
import {scan, map, filter} from 'rxjs/operators';
import {convertPositionV2, convertLeverageData} from '../positions-cache';
import {wsLogout$} from '@/user/auth-ws';
import {subUserIds$} from '@/raw/users';
import {aopMsgAll$, aopMsgV2All$} from '@/raw/aop-all';
import type {ws, ReduceAction} from '@/types';

type ContractPositionRaw = ws.ContractPositionRaw;

type Cache = Map<string, ContractPositionRaw>;
type CacheAll = Map<number, Cache>;
type Action = ReduceAction<Cache>;
type ActionAll = ReduceAction<CacheAll>;

export const positionsAll$ = aopMsgAll$.pipe(
  filter((o) => !!o.data.positions),
  map((o) => ({
    userId: o.userId,
    data: o.data.positions,
  }))
);

export const positionsV2All$ = aopMsgV2All$.pipe(
  filter((o) => !!o.data.positions),
  map((o) => ({
    userId: o.userId,
    data: convertPositionV2(o.data.positions),
  }))
);

function wrapActionByUser(userId: number, action: Action) {
  return function (cache: CacheAll) {
    const userCache = cache.get(userId) || new Map();
    cache.set(userId, action(userCache));
    return cache;
  };
}

const initActionAll$ = subUserIds$.pipe(
  map((userIds) => {
    return (cacheAll: CacheAll) => {
      userIds.forEach((u) => {
        if (cacheAll.get(u)) {
          return;
        }
        cacheAll.set(u, new Map<string, ContractPositionRaw>());
      });
      return cacheAll;
    };
  })
);

const resetActionAll$ = wsLogout$.pipe<ActionAll>(
  map(
    () =>
      function (cache: CacheAll) {
        cache.clear();
        return cache;
      }
  )
);

const updateAction$ = positionsAll$.pipe(
  map((positions): ActionAll => {
    const action = function (cache: Cache) {
      positions.data.forEach((position: ContractPositionRaw) => {
        cache.set(position.symbol, {
          ...cache.get(position.symbol),
          ...position,
          ...convertLeverageData(position, cache.get(position.symbol)),
        });
      });
      return cache;
    };
    return wrapActionByUser(positions.userId, action);
  })
);

const updateAction2$ = positionsV2All$.pipe(
  map((positions): ActionAll => {
    const action = function (cache: Cache) {
      positions.data.forEach((position) => {
        const {symbol, posSide, posMode} = position;
        const key = symbol + posSide;
        ['Merged', 'Long', 'Short'].forEach((side) => {
          const key2 = symbol + side;
          cache.has(key2) && cache.set(key2, {...cache.get(key2), posMode});
        });
        cache.set(key, {
          ...cache.get(key),
          ...position,
          ...convertLeverageData(position, cache.get(key)),
        });
      });
      return cache;
    };
    return wrapActionByUser(positions.userId, action);
  })
);

export const positionsCacheAll$ = merge<ActionAll[]>(
  initActionAll$,
  resetActionAll$,
  updateAction$,
  updateAction2$
).pipe(scan((p, action) => action(p), new Map() as CacheAll));
